import * as React from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { deleteQuizBetType, getQuizBetTypeGroups, getQuizBetTypes, saveQuizBetType } from '../Api';
import {
    QuizBetTypeDto,
    QuizBetTypeGroupOptionDto,
    SaveQuizBetTypeRequest,
} from '../models/dtos/QuizAdminDtos';

const emptyBetType = (): SaveQuizBetTypeRequest => ({
    betTypeId: 0,
    quizBetTypeGroupId: 0,
    name: '',
    nameRu: '',
});

export default function QuizBetTypesPage() {
    const [loading, setLoading] = React.useState(true);
    const [items, setItems] = React.useState<QuizBetTypeDto[]>([]);
    const [groups, setGroups] = React.useState<QuizBetTypeGroupOptionDto[]>([]);
    const [editorOpen, setEditorOpen] = React.useState(false);
    const [deleteId, setDeleteId] = React.useState<number | null>(null);
    const [form, setForm] = React.useState<SaveQuizBetTypeRequest>(emptyBetType());

    const load = React.useCallback(async () => {
        setLoading(true);
        const [betTypes, betTypeGroups] = await Promise.all([getQuizBetTypes(), getQuizBetTypeGroups()]);
        setItems(betTypes);
        setGroups(betTypeGroups);
        setLoading(false);
    }, []);

    React.useEffect(() => {
        load();
    }, [load]);

    const openCreate = () => {
        setForm({
            ...emptyBetType(),
            quizBetTypeGroupId: groups[0]?.id ?? 0,
        });
        setEditorOpen(true);
    };

    const openEdit = (row: QuizBetTypeDto) => {
        setForm({
            id: row.id,
            betTypeId: row.betTypeId ?? 0,
            quizBetTypeGroupId: row.quizBetTypeGroupId ?? 0,
            name: row.name,
            nameRu: row.nameRu,
        });
        setEditorOpen(true);
    };

    const onSave = async () => {
        if (!form.betTypeId || !form.quizBetTypeGroupId) return;
        await saveQuizBetType(form);
        setEditorOpen(false);
        load();
    };

    const confirmDelete = async () => {
        if (deleteId != null) {
            await deleteQuizBetType(deleteId);
            setDeleteId(null);
            load();
        }
    };

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 80 },
        { field: 'betTypeId', headerName: 'Bet type ID', width: 120 },
        { field: 'betTypeName', headerName: 'Bet type', flex: 1 },
        { field: 'quizBetTypeGroupName', headerName: 'Quiz group', flex: 1 },
        { field: 'name', headerName: 'Display name', flex: 1 },
        { field: 'nameRu', headerName: 'Name RU', flex: 1 },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 180,
            sortable: false,
            renderCell: (params) => (
                <Stack direction="row" spacing={1}>
                    <Button size="small" onClick={() => openEdit(params.row)}>Edit</Button>
                    <Button size="small" color="error" onClick={() => setDeleteId(params.row.id)}>Delete</Button>
                </Stack>
            ),
        },
    ];

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h4">Quiz bet types</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Maps real bet types to quiz question groups. Required for the cron to generate questions.
                    </Typography>
                </Box>
                <Button variant="contained" onClick={openCreate}>Add mapping</Button>
            </Stack>
            {loading ? <CircularProgress /> : (
                <DataGrid autoHeight rows={items} columns={columns} getRowId={(row) => row.id} disableRowSelectionOnClick />
            )}

            <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{form.id ? 'Edit bet type mapping' : 'Create bet type mapping'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Bet type ID"
                            type="number"
                            value={form.betTypeId || ''}
                            onChange={(e) => setForm({ ...form, betTypeId: Number(e.target.value) })}
                            fullWidth
                        />
                        <FormControl fullWidth>
                            <InputLabel id="quiz-bet-type-group-label">Quiz bet type group</InputLabel>
                            <Select
                                labelId="quiz-bet-type-group-label"
                                label="Quiz bet type group"
                                value={form.quizBetTypeGroupId || ''}
                                onChange={(e) => setForm({ ...form, quizBetTypeGroupId: Number(e.target.value) })}
                            >
                                {groups.map(group => (
                                    <MenuItem key={group.id} value={group.id}>
                                        {(group.name ?? group.betTypeGroupName ?? 'Group')} (#{group.id})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField label="Display name" value={form.name ?? ''} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
                        <TextField label="Display name RU" value={form.nameRu ?? ''} onChange={(e) => setForm({ ...form, nameRu: e.target.value })} fullWidth />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditorOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={onSave}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteId != null} onClose={() => setDeleteId(null)}>
                <DialogTitle>Delete bet type mapping?</DialogTitle>
                <DialogActions>
                    <Button onClick={() => setDeleteId(null)}>Cancel</Button>
                    <Button color="error" onClick={confirmDelete}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
